import $ from "jquery"
import { debounce } from "../../util/dom"
import { unhyphenate } from "../../util/string"
import fuzzysort from "fuzzysort"

type FilterType = "single" | "array"
interface FilterKey {
  name: string
  type: FilterType
}
type FilterData =
  | ({ type: "single" } & { name: string; value: string })
  | ({ type: "array" } & { name: string; value: string[] })

interface InitOptions<TextKey extends string> {
  textKeys: TextKey[]
  extractText(node: JQuery<HTMLElement>): Record<TextKey, string>
  filterKeys: FilterKey[]
}

type FilterOptions = { name: string; value: string }
type ListItem<TextKey extends string> = {
  index: number
  node: JQuery<HTMLElement>
  texts: Record<TextKey, string>
  filterData: FilterData[]
}

export function initializePublicationSearch() {
  initializeSearchableList(".pubTable", {
    textKeys: ["summary", "date"],
    extractText: (node) => ({
      summary: node.find(".summary").text(),
      date: node.find(".date").text(),
    }),
    filterKeys: [
      { name: "source", type: "single" },
      { name: "topic-types", type: "array" },
    ],
  })
}

function initializeSearchableList<TextKey extends string>(
  selector: JQuery.Selector,
  options: InitOptions<TextKey>
): void {
  $(() => {
    $(selector)
      .find(".searchable-list")
      .map(function () {
        const searchableList = $(this)
        const controls = searchableList.siblings(".searchable-list-controls")
        const emptyMessage = searchableList.children(".searchable-list-empty")
        const searchInput = controls.find(".input > input")
        const filterDropdown = controls.find(".dropdown")

        if (!controls.length) {
          return
        }

        let currentFilter: FilterOptions | null = null

        const itemData: ListItem<TextKey>[] = searchableList
          .children()
          .map(function (index) {
            const node = $(this)
            const texts = options.extractText(node)
            for (const key of options.textKeys) {
              texts[key] = normalizeSearchText(texts[key])
            }
            const filterData = options.filterKeys.map((filterKey) =>
              extractFilterValue(filterKey, node)
            )
            return { index, node, texts, filterData }
          })
          .get()

        filterDropdown.dropdown({
          action: "activate",
          onChange(value, _text, item) {
            if (!value || !item) {
              currentFilter = null
            } else {
              const name = item.data("name")
              currentFilter = { name, value }
            }

            updateResults()
          },
          onActionable(value) {
            if (value === "clear") {
              $(this).dropdown("clear")
            }
          },
        })

        const keys = options.textKeys.map((key) => `texts.${key}`)
        function updateResults() {
          const query = (searchInput.val() as string).trim().toLowerCase()
          const matchingNodeIndices = fuzzysort
            .go(query, itemData, { keys, all: true })
            .map((result) => result.obj.index)

          let hasMatchingPublication = false
          itemData.forEach(function (data) {
            const isSearchMatch =
              matchingNodeIndices === null ||
              matchingNodeIndices.indexOf(data.index) !== -1
            const isFilterMatch = data.filterData.some((filterData) =>
              isMatchingFilter(currentFilter, filterData)
            )
            const isMatchingNode = isSearchMatch && isFilterMatch
            hasMatchingPublication = hasMatchingPublication || isMatchingNode
            data.node.toggle(isMatchingNode)
          })

          emptyMessage.toggle(!hasMatchingPublication)
        }

        searchInput.on(
          "input",
          debounce(() => {
            updateResults()
          }, 50)
        )
      })
  })
}

function normalizeSearchText(text: string) {
  return unhyphenate(text.toLowerCase().trim())
}

function extractFilterValue(
  filterKey: FilterKey,
  node: JQuery<HTMLElement>
): FilterData {
  const data = node.data(filterKey.name)
  const value = typeof data === "string" ? data : ""
  if (filterKey.type === "single") {
    return { name: filterKey.name, type: filterKey.type, value }
  }
  return { name: filterKey.name, type: filterKey.type, value: value.split(",") }
}

function isMatchingFilter(
  filter: FilterOptions | null,
  filterData: FilterData
) {
  if (filter === null) return true
  if (filter.name !== filterData.name) {
    return false
  }

  if (filterData.type === "array") {
    return filterData.value.indexOf(filter.value) !== -1
  }
  return filter.value === filterData.value
}
